import { PageRoutes } from '@/constants/PageRoutes'
import { PageRoutes as DebitPageRoutes } from 'debit-card/constants/PageRoutes'
import { i18n } from 'i18n-utils'
import { StorageKeys, useIpRestrictStore, useUserInfoStore } from 'store'
import { UiUtils } from 'ui-components'
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'

/**
 * 需要登录才能访问的路由列表
 */
const needLogin = [PageRoutes.UserCenter, ...Object.values(DebitPageRoutes)]

/**
 * 访问时自动清除 token 的路由列表
 */
const autoClearTokenRoutes = [
  PageRoutes.LoginInputPassword,
  PageRoutes.RegisterPassword,
  PageRoutes.RegisterAuthEmail,
  PageRoutes.RegisterAuthMobile,
  PageRoutes.Login,
  PageRoutes.Register,
]

/**
 * IP 受限时允许访问的路由白名单
 */
const ipRestrictWhitelist = [PageRoutes.Home, PageRoutes.LegalCompliance]

/**
 * IP 限制守卫
 * @param to 目标路由
 * @param next 导航守卫的 next 函数
 * @returns 是否允许继续导航
 */
function ipRestrictGuard(to: RouteLocationNormalized, next: NavigationGuardNext): boolean {
  const ipRestrictStore = useIpRestrictStore()

  // 如果 IP 受限且不在白名单内，则跳转到首页
  if (ipRestrictStore.isIpRestricted && !ipRestrictWhitelist.includes(to.path as PageRoutes)) {
    next(PageRoutes.Home)
    return false
  }

  return true
}

/**
 * 登录认证守卫
 * @param to 目标路由
 * @param next 导航守卫的 next 函数
 * @returns 是否允许继续导航
 */
function authGuard(to: RouteLocationNormalized, next: NavigationGuardNext): boolean {
  const userInfoStore = useUserInfoStore()

  // 自动清除 token
  if (autoClearTokenRoutes.some((route) => to.path.startsWith(route))) {
    userInfoStore.clearToken()
  }

  // 检查是否需要登录
  const token = window.localStorage.getItem(StorageKeys.Token) || ''
  UiUtils.closeKycDialog?.()
  if (!token && needLogin.some((route) => to.path.startsWith(route))) {
    UiUtils.showError(i18n.global.t('请先登录'))
    next(PageRoutes.Login)
    return false
  }

  return true
}

/**
 * 设置路由守卫
 * @param router Vue Router 实例
 */
export function setupRouterGuards(router: any) {
  router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    // 执行 IP 限制守卫
    if (!ipRestrictGuard(to, next)) {
      return
    }

    // 执行登录认证守卫
    if (!authGuard(to, next)) {
      return
    }

    // 所有守卫通过，允许导航
    next()
  })
}
